import {
  ChevronBottomOutlineIcon,
  ChevronRightOutlineIcon,
  ChevronTopOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import { useRef } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'
import { css, cva, sva } from '~/styled-system/css'
import { flex } from '~/styled-system/patterns'
import type { RecipeVariantProps } from '~/styled-system/types'
import { ReadMore } from '~/ui/ReadMore'

const title = css({ fontSize: { base: '2xl', _pc: '3xl' }, fontWeight: 'bold', color: 'gray.800' })

export function Section(
  props: PropsWithChildren<{
    title: ReactNode
    hiddenContent?: ReactNode
    subContent?: ReactNode
    titleSideContent?: ReactNode
  }> &
    RecipeVariantProps<typeof SectionStyles>
) {
  const ref = useRef<HTMLElement>(null)
  const withPadding = props.withPadding
  const { container } = SectionStyles({ withPadding })
  return (
    <section className={container} ref={ref}>
      {props.titleSideContent !== undefined ? (
        <div className={flex({ justifyContent: 'space-between' })}>
          <h2 className={title}>{props.title}</h2>
          {props.titleSideContent}
        </div>
      ) : (
        <h2 className={title}>{props.title}</h2>
      )}
      {props.children}
      {props.subContent !== undefined && (
        <div className={css({ spaceY: '1' })}>{props.subContent}</div>
      )}
      {props.hiddenContent !== undefined && (
        <nav>
          <ReadMore openLabel={<OpenLabel />} closeLabel={<CloseLabel />} scrollTargetRef={ref}>
            <div className={css({ spaceY: '4', py: { base: '3', _pc: '5' }, borderTop: 'light' })}>
              {props.hiddenContent}
            </div>
          </ReadMore>
        </nav>
      )}
    </section>
  )
}

const chevron = cva({
  variants: {
    color: {
      primary: { color: 'primary' },
      link: { color: 'link' },
    },
  },
  defaultVariants: { color: 'primary' },
})

// コンテンツがスクロールするかどうかで padding を変える
const SectionStyles = sva({
  slots: ['container', 'title'],
  base: {
    container: {
      spaceY: { base: '3', _pc: '5' },
      _sd: { py: '4' },
    },
    title: flex.raw({ align: 'center', gap: '1' }),
  },
  variants: {
    withPadding: {
      true: {
        container: { _sd: { px: '3' } },
      },
      false: {
        title: { px: '3' },
      },
    },
  },
  defaultVariants: { withPadding: true },
})

/**
 * タイトル用の右向き chevron つきのリンク
 * font-size などは外から設定する
 */
export function TitleLink(
  props: { title: string; href: string } & RecipeVariantProps<typeof chevron> &
    RecipeVariantProps<typeof SectionStyles>
) {
  const withPadding = props.withPadding
  const { title } = SectionStyles({ withPadding })
  return (
    <a href={props.href} className={title}>
      {props.title}
      <ChevronRightOutlineIcon className={chevron(props)} />
    </a>
  )
}

const label = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: { base: 'center', _pc: 'end' },
  fontSize: { base: 'sm', _pc: 'base' },
  color: 'link',
  gap: '1',
  _pc: { cursor: 'pointer' },
})

function OpenLabel() {
  return (
    <div className={label}>
      もっとみる
      <ChevronBottomOutlineIcon />
    </div>
  )
}

function CloseLabel() {
  return (
    <div className={label}>
      閉じる
      <ChevronTopOutlineIcon />
    </div>
  )
}
